import ApiService from '../../components/api_services/apiService.jsx';
import ActionMenu from '../../components/components/action_menu/ActionMenu.jsx';

import { ReactComponent as StatusUp } from '../../components/icons/status-up.svg';
import { ReactComponent as Edit } from '../../components/icons/edit.svg';
import { ReactComponent as TaskSquare } from '../../components/icons/task-square.svg';
import { ReactComponent as Pause } from '../../components/icons/pause.svg';
import { ReactComponent as Play } from '../../components/icons/play.svg';
import { ReactComponent as TickCircle } from '../../components/icons/tick-circle.svg';
import { ReactComponent as Trash } from '../../components/icons/trash.svg';
import { ReactComponent as Danger } from '../../components/icons/danger.svg';

import {
  toggleCheckedItem,
  openEditModal,
  toggleAddModal,
  setEditedEvent,
  setAddEventMode,
  setDuplicatedEventId,
  setModalViewActualTab,
  toggleNeedRefresh,
  setPendingEditReview,
  clearPendingEditReview,
} from '../../store/eventsSlice';
import { setSelectedEvent } from '../../store/statsSlice.js';
import EventPromotionBadge from './EventPromotionBadge.jsx';
import { getPromotionForEvent } from './eventPromotionLookup.js';

const formatDate = (utcString) => {
  if (!utcString) {
    return '-';
  }

  const date = new Date(utcString);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleString('pl-PL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isApprovedByAdmin = (event) => {
  if (typeof event?.approvedByAdmin === 'boolean') {
    return event.approvedByAdmin;
  }
  return event?.isApprovedByAdmin;
};

const isEventActive = (event) => {
  if (typeof event?.active === 'boolean') {
    return event.active;
  }
  return event?.isActive;
};

const getEventStatusLabel = (event) => {
  if (event?.hasPendingEdit === true) {
    return 'Zmiana danych';
  }

  if (isApprovedByAdmin(event) === false) {
    return 'Oczekujace';
  }

  return isEventActive(event) ? 'Aktywne' : 'Nieaktywne';
};

const getEventStatusClassName = (event) => {
  if (event?.hasPendingEdit === true) {
    return 'event-status event-status--data-change';
  }

  if (isApprovedByAdmin(event) === false) {
    return 'event-status event-status--pending';
  }

  if (!isEventActive(event)) {
    return 'event-status event-status--inactive';
  }

  return 'event-status event-status--active';
};

const promptRejectReason = (initialReason = '') => {
  const value = window.prompt('Podaj powod odrzucenia:', initialReason);
  if (value === null) {
    return null;
  }

  const reason = value.trim();
  if (!reason) {
    alert('Powod odrzucenia jest wymagany.');
    return null;
  }

  return reason;
};

const getDefaultEventActions = (event, dispatch, onDeleteEvent) => ([
  {
    key: 'stats',
    label: 'Statystyki',
    icon: StatusUp,
    onClick: () => {
      dispatch(setSelectedEvent(event.id));
      window.location.href = '/statistics';
    },
  },
  {
    key: 'edit',
    label: 'Edytuj',
    icon: Edit,
    onClick: () => {
      dispatch(setAddEventMode(false));
      dispatch(clearPendingEditReview());
      dispatch(setEditedEvent(event.id));
      dispatch(openEditModal());
    },
  },
  {
    key: 'duplicate',
    label: 'Duplikuj',
    icon: TaskSquare,
    onClick: () => {
      dispatch(setAddEventMode(true));
      dispatch(clearPendingEditReview());
      dispatch(setModalViewActualTab('Dane podstawowe'));
      dispatch(setEditedEvent(null));
      dispatch(setDuplicatedEventId(event.id));
      dispatch(toggleAddModal());
    },
  },
  {
    key: 'deactivate',
    label: isEventActive(event) ? 'Deaktywuj' : 'Aktywuj',
    icon: isEventActive(event) ? Pause : Play,
    onClick: async () => {
      try {
        await ApiService.updateEvent(event.id, { isActive: !isEventActive(event) });
        dispatch(toggleNeedRefresh());
      } catch (error) {
        console.error('Blad zmiany statusu wydarzenia:', error);
      }
    },
  },
  ...(isApprovedByAdmin(event) === false
    ? [
        {
          key: 'approve',
          label: 'Zaakceptuj',
          icon: TickCircle,
          onClick: async () => {
            try {
              await ApiService.approveEvent(event.id);
              dispatch(toggleNeedRefresh());
            } catch (error) {
              console.error('Blad akceptacji wydarzenia:', error);
            }
          },
        },
        {
          key: 'reject',
          label: 'Odrzuc',
          icon: Danger,
          danger: true,
          onClick: async () => {
            const reason = promptRejectReason(
              String(event.rejectReason ?? event.rejectionReason ?? event.reason ?? '').trim()
            );
            if (!reason) {
              return;
            }

            try {
              await ApiService.rejectEvent({ id: String(event.id), reason });
              dispatch(toggleNeedRefresh());
            } catch (error) {
              console.error('Blad odrzucenia wydarzenia:', error);
            }
          },
        },
      ]
    : []),
  {
    key: 'delete',
    label: 'Usun',
    icon: Trash,
    danger: true,
    onClick: () => onDeleteEvent(event.id),
  },
]);

const getPendingEditActions = (event, dispatch) => ([
  {
    key: 'approve-pending-edit',
    label: 'Zaakceptuj zmiane',
    icon: TickCircle,
    onClick: async () => {
      try {
        const pendingEdits = await ApiService.getPendingEventEdits();
        const pendingItem = (Array.isArray(pendingEdits) ? pendingEdits : []).find(
          (item) => String(item?.eventId) === String(event.id)
        );

        let payload = {};
        const rawPayload = pendingItem?.pendingChange?.payloadJson;
        if (rawPayload && typeof rawPayload === 'object') {
          payload = rawPayload;
        } else {
          try {
            payload = JSON.parse(rawPayload || '{}');
            if (typeof payload === 'string') {
              payload = JSON.parse(payload || '{}');
            }
          } catch (parseError) {
            payload = {};
          }
        }

        dispatch(setAddEventMode(false));
        dispatch(setModalViewActualTab('Dane podstawowe'));
        dispatch(setEditedEvent(event.id));
        dispatch(clearPendingEditReview());
        dispatch(setPendingEditReview({
          eventId: event.id,
          pendingId: pendingItem?.id ?? pendingItem?.eventId ?? event.id,
          payload,
          requestedAt: pendingItem?.pendingChange?.requestedAt ?? null,
        }));
        dispatch(openEditModal());
      } catch (error) {
        console.error('Blad akceptacji zmiany wydarzenia:', error);
      }
    },
  },
  {
    key: 'reject-pending-edit',
    label: 'Odrzuc zmiane',
    icon: Danger,
    danger: true,
    onClick: async () => {
      const reason = promptRejectReason();
      if (!reason) {
        return;
      }

      try {
        await ApiService.rejectPendingEventEdit({ id: event.id, reason });
        dispatch(toggleNeedRefresh());
      } catch (error) {
        console.error('Blad odrzucenia zmiany wydarzenia:', error);
      }
    },
  },
]);

export const fetchUsers = async (
  sortBy,
  sortDirection,
  currentPage,
  dispatch,
  setMaxPages,
  setRows,
  checkedItems,
  keyword,
  onDeleteEvent,
  filterQuery = 'ALL',
  promotionByEventId
) => {
  try {
    const checkedSortBy = sortBy === null || sortDirection === null ? null : sortBy;
    const checkedSortDirection = sortBy === null || sortDirection === null ? null : sortDirection;
    const responseCategories = await ApiService.getEventsCategories();
    const categories = responseCategories.data;
    const response = await ApiService.getEvents(checkedSortBy, checkedSortDirection, currentPage, keyword, filterQuery);
    const eventsData = Array.isArray(response?.data) ? response.data : [];

    dispatch(setMaxPages(response?.maxPage ?? 1));

    const mappedRows = eventsData.map((event) => [
      <label
        key={`checkbox-${event.id}`}
        className="checkbox-wrapper"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={checkedItems && checkedItems.includes(event.id)}
          onChange={() => dispatch(toggleCheckedItem(event.id))}
        />
      </label>,
      event.id,
      <div>
        <ActionMenu
          items={event?.hasPendingEdit === true ? getPendingEditActions(event, dispatch) : getDefaultEventActions(event, dispatch, onDeleteEvent)}
        />
      </div>,
      formatDate(event.pendingEditRequestedAt ?? event.pendingRequestedAt ?? event.added_on_utc),
      event.name,
      <div>
        {event.city} - {event.place}
      </div>,
      formatDate(event.startDate),
      formatDate(event.endDate),
      categories.find((cat) => cat.id === event.categoryId)?.name || 'Brak kategorii',
      <div className="event-status-group" key={`status-${event.id}`}>
        <span className={getEventStatusClassName(event)}>{getEventStatusLabel(event)}</span>
        <EventPromotionBadge promotion={getPromotionForEvent(promotionByEventId, event.id)} />
      </div>,
    ]);

    setRows(mappedRows);
  } catch (error) {
    console.error('Blad podczas pobierania danych:', error);
  }
};
